import { IGameAnimatedIconsManagerService, IGameAnimatedIconRegistrationResult } from "./game-animated-icons-manager.service.interface";
import { IServiceFactoryExtended } from "../service-factory-extended.interface";
import { shuffle } from "../../../utils/array-helper";
import {
    AnimatedIconBaseService,
    AnimatedIconObservableTarget,
    AnimatedIconUnloadReason, IAnimatedIconRequestOptions
} from "../pixi-app/animated-icon-base-service.service";

const MAX_ANIMATED_ICONS = 2;

/**
 * This service will manage game animated icons loaded with pixi-spine.
 *
 */
export class GameAnimatedIconsManagerService extends AnimatedIconBaseService implements IGameAnimatedIconsManagerService {
    private intersectionObserver: IntersectionObserver | null = null;
    private observedTargets = new Map<Element, AnimatedIconObservableTarget>();
    private isGameOpened: boolean = false;

    constructor(services: IServiceFactoryExtended) {
        super(services);
        this.intersectionObserver = new IntersectionObserver(this.intersectionCallback, {
            threshold: 0.7
        });
        /**
         * When the game opens, the content is not visible so stop all animations
         */
        services.sdk.events.onGameOpen(() => {
            this.isGameOpened = true;
            this.observedTargets.forEach((observedTarget) => {
                this.attemptDisposeAnimation(observedTarget, AnimatedIconUnloadReason.GAME_OPEN);
            });
        });
        /**
         * When the game opens, the content is visible so load animations
         */
        services.sdk.events.onGameClose(() => {
            this.isGameOpened = false;
            this.selectAndLoadAnimations();
        });
    }

    private intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        entries.forEach((entry) => {
            if (!this.observedTargets.has(entry.target)) {
                // should throw error ?
                return;
            }
            const observedTarget = this.observedTargets.get(entry.target);
            if (entry.isIntersecting) {
                // mark the observedTarget as eligible for receiving animation
                observedTarget.inViewport = true;
            } else {
                // unmark the observedTarget as eligible targets
                observedTarget.inViewport = false;
                // destroy the animation if it exists
                this.attemptDisposeAnimation(observedTarget, AnimatedIconUnloadReason.VIEWPORT_EXIT);
            }
        });
        this.selectAndLoadAnimations();
    };

    /**
     * Method to register the game animated icon so it can be managed by this.
     */
    public register(options: IAnimatedIconRequestOptions): IGameAnimatedIconRegistrationResult {
        this.observedTargets.set(options.wrapper, options);
        this.intersectionObserver.observe(options.wrapper);

        return {
            unregister: () => {
                if (this.observedTargets.has(options.wrapper)) {
                    const observedTarget = this.observedTargets.get(options.wrapper)
                    this.observedTargets.delete(options.wrapper);
                    this.intersectionObserver.unobserve(options.wrapper);
                    this.attemptDisposeAnimation(observedTarget, AnimatedIconUnloadReason.REQUESTED);
                }
            }
        };
    }
    private debounceTimeout: number | undefined;
    private selectionRunning: boolean = false;
    private selectionRunAgainNeeded: boolean = false;
    private selectAndLoadAnimations = () => {
        // we cannot run the selection twice in paralel, it has to be sequential
        if (this.selectionRunning) {
            this.selectionRunAgainNeeded = true;
            return;
        }
        // avoid choking the CPU with too many subsequent tasks, debounce
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
            this.debounceTimeout = undefined;
        }
        this.debounceTimeout = setTimeout(async () => {
            this.selectionRunning = true;

            // When a game is opened, we don't load any animation
            if (!this.isGameOpened) {
                let alreadyRunningAnimationCount = 0;
                let eligibleTargets: AnimatedIconObservableTarget[] = [];
                // count already running animations and count eligible targets
                this.observedTargets.forEach((observedTarget) => {
                    if (observedTarget.animationDisposer || observedTarget.loadingInProgress) {
                        alreadyRunningAnimationCount++;
                    }
                    if (observedTarget.inViewport && !observedTarget.animationDisposer && !observedTarget.loadingInProgress) {
                        eligibleTargets.push(observedTarget);
                    }
                });

                // if we are not already running the maximum allowed animations we take as much as we can from the eligibles targets
                if (alreadyRunningAnimationCount > MAX_ANIMATED_ICONS) {
                    this.services.logger.error("There are more animated icons running ("+alreadyRunningAnimationCount+") than MAX_ANIMATED_ICONS ("+MAX_ANIMATED_ICONS+")");
                } else if (alreadyRunningAnimationCount < MAX_ANIMATED_ICONS) {
                    let remainingSlots = MAX_ANIMATED_ICONS - alreadyRunningAnimationCount;
                    shuffle(eligibleTargets);
                    for (let i=0; i<Math.min(eligibleTargets.length, remainingSlots); i++) {
                        const observedTarget = eligibleTargets[i];
                        this.loadAnimatedIcon(observedTarget, "gameAnimatedIconResource");
                    }
                }
            }

            this.debounceTimeout = undefined;
            this.selectionRunning = false;
            if (this.selectionRunAgainNeeded) {
                this.selectionRunAgainNeeded = false;
                this.selectAndLoadAnimations();
            }
        }, 400);
    }
}
